import { useQuery, useSubscription } from '@apollo/client';

import {
  CartOperationResponse,
  CartOrder,
  CartResponse,
  CurrentUser,
  GetCartInput,
  GetUserCartOrdersInput,
  PromoCodeResponse,
  UserCartOrdersResult,
} from '../TypeDefs';
import {
  GET_CART,
  GET_CART_METADATA,
  GET_CART_ORDER,
  GET_PROMO_CODE,
  GET_USER_CART_ORDERS,
  GET_USER_PAYMENT_METHODS,
  CART_ORDER_PROCESSING_COMPLETE_SUBSCRIPTION,
  CART_ORDER_UPDATES_SUBSCRIPTION,
} from '../graphql/queries';

// Hook for getting a specific cart order by ID
export const useCartOrder = (orderId: string, options?: { skip?: boolean }) => {
  return useQuery<{ getCartOrder: CartOrder }>(GET_CART_ORDER, {
    variables: { orderId },
    skip: options?.skip || !orderId,
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
};

// Hook for getting user's cart orders
export const useUserCartOrders = (
  input: GetUserCartOrdersInput,
  options?: {
    skip?: boolean;
    fetchPolicy?: 'cache-first' | 'cache-and-network' | 'network-only' | 'no-cache';
  }
) => {
  return useQuery<{ getUserCartOrders: UserCartOrdersResult }>(
    GET_USER_CART_ORDERS,
    {
      variables: { input },
      skip: options?.skip,
      errorPolicy: 'all',
      fetchPolicy: options?.fetchPolicy || 'no-cache',
      notifyOnNetworkStatusChange: true,
    }
  );
};

// Hook for getting user's payment methods
export const useUserPaymentMethods = (options?: { skip?: boolean }) => {
  return useQuery<{ currentUser: CurrentUser }>(GET_USER_PAYMENT_METHODS, {
    skip: options?.skip,
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
  });
};

// Hook for getting applicable promo codes
export const useApplicablePromoCodes = (options?: { skip?: boolean }) => {
  return useQuery<{ getApplicablePromoCodes: PromoCodeResponse }>(
    GET_PROMO_CODE,
    {
      skip: options?.skip,
      errorPolicy: 'all',
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-first',
    }
  );
};

// Hook for getting cart items
export const useCartItems = (
  input: GetCartInput,
  options?: {
    skip?: boolean;
    fetchPolicy?:
      | 'cache-first'
      | 'cache-and-network'
      | 'network-only'
      | 'cache-only'
      | 'no-cache'
      | 'standby';
    nextFetchPolicy?:
      | 'cache-first'
      | 'cache-and-network'
      | 'network-only'
      | 'cache-only'
      | 'no-cache'
      | 'standby';
    notifyOnNetworkStatusChange?: boolean;
  }
) => {
  return useQuery<{ getCart: CartResponse }>(GET_CART, {
    variables: { input },
    skip: options?.skip,
    errorPolicy: 'all',
    fetchPolicy: options?.fetchPolicy || 'network-only',
    nextFetchPolicy: options?.nextFetchPolicy || 'network-only',
    notifyOnNetworkStatusChange: options?.notifyOnNetworkStatusChange,
  });
};

// Hook for getting cart metadata (including applied promocode)
export const useCartMetadata = (options?: { skip?: boolean }) => {
  return useQuery<{ getCartMetadata: CartOperationResponse }>(
    GET_CART_METADATA,
    {
      skip: options?.skip,
      errorPolicy: 'all',
      fetchPolicy: 'cache-and-network',
    }
  );
};

// Utility hook that combines multiple queries for cart checkout
export const useCartCheckoutData = (orderId: string) => {
  const cartOrderQuery = useCartOrder(orderId);
  const paymentMethodsQuery = useUserPaymentMethods();

  return {
    cartOrder: cartOrderQuery.data?.getCartOrder,
    paymentMethods: paymentMethodsQuery.data?.currentUser?.paymentMethods || [],
    loading: cartOrderQuery.loading || paymentMethodsQuery.loading,
    error: cartOrderQuery.error || paymentMethodsQuery.error,
    refetchCartOrder: cartOrderQuery.refetch,
    refetchPaymentMethods: paymentMethodsQuery.refetch,
  };
};

// Hook for subscribing to cart order processing completion notifications
export const useCartOrderProcessingCompleteSubscription = (
  input?: { userId?: string; orderId?: string },
  options?: {
    skip?: boolean;
    onSubscriptionData?: (data: any) => void;
  }
) => {
  return useSubscription(CART_ORDER_PROCESSING_COMPLETE_SUBSCRIPTION, {
    variables: { input },
    skip: options?.skip,
    onSubscriptionData: options?.onSubscriptionData,
  });
};

export const useCartOrderUpdatesSubscription = (
  input?: { userId?: string; orderId?: string },
  options?: {
    skip?: boolean;
    onSubscriptionData?: (data: any) => void;
  }
) => {
  return useSubscription(CART_ORDER_UPDATES_SUBSCRIPTION, {
    variables: { input },
    skip: options?.skip,
    onSubscriptionData: options?.onSubscriptionData,
  });
};
